import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { VendorResponse, VendorUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper, EMPTY_GUID } from '../utils';
import { VendorDetailsSectionHandler } from './details.section';
import { COMPANY_ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { SharedAddressSectionHandler } from '../shared/address.section';
import { VendorCustomizeSectionHandler } from './customize.section';
import { VendorSettingsSectionHandler } from './settings.section';
import { HttpClient } from '@angular/common/http';
import { SoftLimitSectionHandler } from '../shared/limits.section';
import { OidcIssuersSectionHandler } from '../shared/oidc-issuers.section';
import { nullAccessDenied } from 'app/services';
import {  buildChartSectionGroup } from '../shared/chart.section';
import { VendorNotificationsSectionHandler } from './notifications.section';
import { RetentionSectionHandler } from '../shared/retention.section';
import { PasswordPolicySectionHandler } from '../shared/password-policy.section';
import { SharedSslCertificatesSectionHandler } from '../shared/sslcertificate.section';

@RegisterEntityHandler('vendor')
@Injectable()
export class VendorHandler extends EntityHandler<VendorResponse> {

    constructor(
        admin: AdminEditorService,
        private httpClient: HttpClient
    ) {
        super(admin);
    }

    states = ['inactive', 'active', 'suspended', 'deleted'];
    counts = ['client', 'asset', 'device', 'companion-camera', 'user'];


    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listVendors/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.accounts.listVendors(ownerId, offset, limit, sort, filter, this.counts.join(',')), 3).then(response => ({
            ...response,
            type: 'vendor',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.VENDORS'),
        }));
    }

    getEntity(id: string): Promise<VendorResponse> {
        return this.admin.getCached(`getVendor/${id}`, api => api.accounts.getVendor(id), 10);
    }

    createEntity(ownerId: string, entity: any): Promise<VendorResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.accounts.createVendor(entity), { _navigation: 'vendor' });
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.accounts.updateVendor(entity.id, { state: state as any }), { _navigation: 'vendor' });
    }

    async getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {

        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.VENDOR.COLUMNS');

        const companyGroups = await this.admin.getCached(`listCompanyGroups/${ownerId}`, api => api.accounts.listCompanyGroups(ownerId)).catch(nullAccessDenied);

        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', COMPANY_ICONS.vendor),
            grid.TextColumn('website', 'WEBSITE'),
            companyGroups ? grid.ComboColumn('group', 'GROUP', [{ id: EMPTY_GUID, value: this.admin.i18n.instant('SHARED.NONE') }, ...companyGroups.items.map(x => ({ id: x.id, value: x.name }))], 80, true) : null,
            grid.StateColumn(this.states),
            ...this.counts.map(count => grid.NumberColumn(`counts.${count}`, count.replace(/-/g, '_').toUpperCase(), 60)),
            grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
        ].filter(x => x);

        return Promise.resolve(columns);

    }

    getEditorSections(ownerId: string, _entity: VendorResponse): Promise<SectionHandlerGroup<VendorResponse>[]> {
        const sections: SectionHandlerGroup<VendorResponse>[] = [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new VendorDetailsSectionHandler(this.admin, ownerId),
                new VendorCustomizeSectionHandler(this.admin, ownerId, this.httpClient),
                new SharedAddressSectionHandler(this.admin, ownerId, 'vendor'),
                new SharedSslCertificatesSectionHandler(this.admin, ownerId),
            ],
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.SETTINGS'),
            sections: [
                new VendorSettingsSectionHandler(this.admin, ownerId),
                new VendorNotificationsSectionHandler(this.admin, ownerId),
                this.admin.flags.otpTestingEnabled() ? new PasswordPolicySectionHandler<VendorResponse>(this.admin, ownerId, 'vendor') : null, 
                new OidcIssuersSectionHandler<VendorResponse>(this.admin, ownerId, 'vendor'),
                this.admin.user.owner.type === 'system' && new SoftLimitSectionHandler<VendorResponse>(this.admin, ownerId, 'vendor'),
                this.admin.user.owner.type === 'system' ? new RetentionSectionHandler<VendorResponse>(this.admin, ownerId, 'vendor') : null,
            ].filter(x => x),
        }];

        sections.push(buildChartSectionGroup(this.admin, 'vendor', this.counts, ['7d', '30d', '60d', '90d']));

        return Promise.resolve(sections);

    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: VendorUpdateRequest, sections: FormSectionHandler<VendorResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'vendor',
            entity: {}, // set up some defaults if you want
            sections: [
                new VendorDetailsSectionHandler(this.admin, ownerId),
            ],
        });
    }


}













